import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { admin } from '../../lib/api';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Users, DollarSign, Activity, Calendar, AlertTriangle, CreditCard } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#2B7CFF', '#84CC16', '#00D4FF', '#EC4899', '#6366F1'];

const AdminDashboard = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const tabFromPath = (pathname) => {
    if (pathname === '/admin/users') return 'users';
    if (pathname === '/admin/payments') return 'payments';
    return 'overview';
  };
  const [analytics, setAnalytics] = useState(null);
  const [payments, setPayments] = useState([]);
  const [costUsage, setCostUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => tabFromPath(location.pathname));

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setActiveTab(tabFromPath(location.pathname));
  }, [location.pathname]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'users') {
      navigate('/admin/users');
      return;
    }
    if (tab === 'payments') {
      navigate('/admin/payments');
      return;
    }
    navigate('/admin/dashboard');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, paymentsRes, costUsageRes] = await Promise.all([
        admin.getAnalytics(),
        admin.getPayments(),
        admin.getCostUsage().catch(() => ({ data: null }))
      ]);
      setAnalytics(analyticsRes.data);
      setPayments(paymentsRes.data);
      setCostUsage(costUsageRes?.data || null);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const roleData = analytics?.users_by_role
    ? Object.entries(analytics.users_by_role).map(([role, count]) => ({
        name: role === 'athlete' ? t('admin.athletes') : role === 'coach' ? t('admin.coaches') : t('admin.admins'),
        value: count
      }))
    : [];

  const subscriptionData = analytics?.subscriptions_by_plan
    ? Object.entries(analytics.subscriptions_by_plan).map(([plan, count]) => ({
        name: plan === 'free' ? 'Free' : plan === 'pro' ? 'Pro' : plan === 'coach' ? 'Coach' : 'Club',
        value: count
      }))
    : [];

  const recentPayments = payments.slice(-10).reverse();
  const budget = costUsage?.budget || {};
  const totals = costUsage?.totals || {};
  const featureRows = costUsage?.per_feature || [];
  const budgetStatusClass = budget.status === 'critical'
    ? 'bg-red-100 text-red-700'
    : budget.status === 'warning'
      ? 'bg-yellow-100 text-yellow-700'
      : 'bg-green-100 text-green-700';

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0">
      <div className="mb-8">
        <h1 className="text-3xl font-light tracking-tight text-primary">{t('admin.title')}</h1>
        <p className="text-text-muted mt-1">{t('admin.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-3 mb-6 border-b border-gray-200">
        <button
          onClick={() => handleTabChange('overview')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'overview'
              ? 'text-primary border-b-2 border-primary'
              : 'text-text-muted hover:text-primary'
          }`}
        >
          {t('admin.overview')}
        </button>
        <button
          onClick={() => handleTabChange('payments')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'payments'
              ? 'text-primary border-b-2 border-primary'
              : 'text-text-muted hover:text-primary'
          }`}
        >
          {t('admin.payments')}
        </button>
        <button
          onClick={() => handleTabChange('users')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'users'
              ? 'text-primary border-b-2 border-primary'
              : 'text-text-muted hover:text-primary'
          }`}
        >
          {t('admin.users')}
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">{t('admin.totalUsers')}</p>
                  <p className="text-3xl font-medium text-primary mt-1">
                    {analytics?.total_users || 0}
                  </p>
                </div>
                <Users className="text-primary" size={32} />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">{t('admin.totalTrainings')}</p>
                  <p className="text-3xl font-medium text-primary mt-1">
                    {analytics?.total_trainings || 0}
                  </p>
                </div>
                <Activity className="text-primary" size={32} />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">{t('admin.totalMatches')}</p>
                  <p className="text-3xl font-medium text-primary mt-1">
                    {analytics?.total_matches || 0}
                  </p>
                </div>
                <Calendar className="text-primary" size={32} />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">{t('admin.revenue')}</p>
                  <p className="text-3xl font-medium text-primary mt-1">
                    {analytics?.total_revenue ? `${analytics.total_revenue.toLocaleString('ru-RU')} ₽` : '0 ₽'}
                  </p>
                </div>
                <DollarSign className="text-primary" size={32} />
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Users by Role */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-medium mb-4">{t('admin.usersByRole')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={roleData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {roleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Subscriptions */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-medium mb-4">{t('admin.subscriptionsByPlan')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={subscriptionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#2B7CFF" name={t('admin.count')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* AI Cost & Usage Controls */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">{t('admin.aiCostControl')}</h3>
              <span className={`px-2 py-1 rounded text-xs font-medium ${budgetStatusClass}`}>
                {t(`admin.budgetStatus.${budget.status || 'ok'}`)}
              </span>
            </div>

            {!costUsage ? (
              <p className="text-text-muted">{t('admin.aiCostUnavailable')}</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-xs text-text-muted ">{t('admin.actualSpend')}</p>
                    <p className="text-xl font-medium">${(totals.actual_cost_usd || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-xs text-text-muted ">{t('admin.monthlyForecast')}</p>
                    <p className="text-xl font-medium">${(totals.monthly_forecast_usd || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-xs text-text-muted ">{t('admin.budgetCap')}</p>
                    <p className="text-xl font-medium">${(budget.monthly_budget_usd || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-xs text-text-muted ">{t('admin.aiRequests')}</p>
                    <p className="text-xl font-medium">{totals.openai_requests || 0}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle size={16} className={budget.status === 'critical' ? 'text-red-600' : 'text-yellow-600'} />
                  <span className="text-text-muted">
                    {t('admin.budgetUtilization')}: {totals.actual_budget_utilization_pct || 0}% ·
                    {` `}
                    {t('admin.forecastUtilization')}: {totals.forecast_budget_utilization_pct || 0}%
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">{t('admin.feature')}</th>
                        <th className="px-3 py-2 text-left">{t('admin.requests')}</th>
                        <th className="px-3 py-2 text-left">{t('admin.tokens')}</th>
                        <th className="px-3 py-2 text-left">{t('admin.estimatedCost')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {featureRows.slice(0, 8).map((row) => (
                        <tr key={row.feature}>
                          <td className="px-3 py-2 font-medium">{row.feature}</td>
                          <td className="px-3 py-2">
                            {row.openai_requests} / {row.requests}
                          </td>
                          <td className="px-3 py-2">
                            {(row.input_tokens || 0) + (row.output_tokens || 0)}
                          </td>
                          <td className="px-3 py-2">${(row.estimated_cost_usd || 0).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Detailed Stats */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-medium mb-4">{t('admin.detailedStats')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.athletes')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.users_by_role?.athlete || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.coaches')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.users_by_role?.coach || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.admins')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.users_by_role?.admin || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.freePlan')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.subscriptions_by_plan?.free || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.proPlan')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.subscriptions_by_plan?.pro || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.coachPlan')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.subscriptions_by_plan?.coach || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.clubPlan')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.subscriptions_by_plan?.club || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">{t('admin.totalMatchesLabel')}</p>
                <p className="text-2xl font-medium">
                  {analytics?.total_matches || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium">{t('admin.paymentHistory')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted ">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted ">
                    {t('admin.user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted ">
                    {t('admin.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted ">
                    {t('admin.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted ">
                    {t('admin.method')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted ">
                    {t('common.date')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {recentPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-text-muted">
                      {payment.id.slice(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      {payment.user_id.slice(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      {payment.amount.toLocaleString(i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB')} {payment.currency}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        payment.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-text-secondary'
                      }`}>
                        {payment.status === 'completed' ? t('admin.paymentCompleted') :
                         payment.status === 'pending' ? t('admin.paymentPending') :
                         payment.status === 'failed' ? t('admin.paymentFailed') : t('admin.paymentRefunded')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-muted capitalize">
                      {payment.payment_method}
                    </td>
                    <td className="px-6 py-4 text-sm text-text-muted">
                      {new Date(payment.created_at).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {payments.length === 0 && (
              <div className="text-center py-12">
                <CreditCard className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-text-muted">{t('admin.noPayments')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium">{t('admin.userManagement')}</h3>
            <button
              type="button"
              disabled
              className="px-4 py-2 bg-gray-200 text-text-muted rounded-md cursor-not-allowed"
              title={t('admin.inDevelopment')}
            >
              {t('admin.addUser')}
            </button>
          </div>
          <div className="text-center py-12">
            <Users className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-text-muted mb-4">{t('admin.inDevelopment')}</p>
            <p className="text-sm text-text-muted">
              {t('admin.userListHint')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
